import { startAuthentication, startRegistration, WebAuthnAbortService } from '@simplewebauthn/browser';

import { api } from '../api/client';

/**
 * 패스키(WebAuthn) 클라이언트 흐름.
 * 서버가 옵션을 내면 브라우저 API로 인증기(Face ID·지문·기기 PIN)를 호출하고,
 * 그 결과를 서버에 돌려 검증받는다. 개인키는 기기 밖으로 나가지 않는다.
 */

/** 이 브라우저가 패스키를 지원하는가. 미지원이면 관련 UI를 숨긴다. */
export function passkeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator?.credentials?.create === 'function'
  );
}

/** 사용자가 취소하면(NotAllowedError/AbortError) 조용히 넘긴다. */
export function isUserCancel(err: unknown): boolean {
  return err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

interface LoginResult {
  ok: true;
  next: 'home' | 'onboarding';
}

/**
 * 브라우저가 Conditional UI(자동완성 패스키)를 지원하는가.
 * 크롬(안드로이드·데스크톱)·사파리 등 최신 브라우저에서 true.
 */
export async function conditionalMediationAvailable(): Promise<boolean> {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential === 'function' &&
      typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
      (await window.PublicKeyCredential.isConditionalMediationAvailable())
    );
  } catch {
    return false;
  }
}

/**
 * 자동완성 패스키 로그인. 아이디 입력창의 자동완성에서 사용자가 패스키를 고르면 resolve.
 * 패스키가 없으면(신규 방문자) 아무 일도 일어나지 않는다 — QR 트랩이 없다.
 * 취소·중단·실패는 조용히 null(이메일 코드 경로가 있으므로).
 */
export async function startConditionalPasskeyLogin(): Promise<LoginResult | null> {
  try {
    const options = await api.post<
      import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON
    >('/auth/passkey/login/options');
    const response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
    return await api.post<LoginResult>('/auth/passkey/login/verify', { response });
  } catch {
    return null;
  }
}

/** 진행 중인 패스키 자동완성 요청을 취소한다(화면 이탈·폼 제출 시). */
export function cancelPasskeyCeremony(): void {
  try {
    WebAuthnAbortService.cancelCeremony();
  } catch {
    /* 진행 중 없음 */
  }
}

/** 로그인. 성공하면 세션 쿠키가 심긴다. */
export async function loginWithPasskey(): Promise<LoginResult> {
  const options = await api.post<import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON>(
    '/auth/passkey/login/options',
  );
  const response = await startAuthentication({ optionsJSON: options });
  return api.post<LoginResult>('/auth/passkey/login/verify', { response });
}

/** 현재 로그인된 계정에 이 기기의 패스키를 등록한다. */
export async function registerPasskey(label?: string): Promise<void> {
  const options = await api.post<import('@simplewebauthn/browser').PublicKeyCredentialCreationOptionsJSON>(
    '/api/passkeys/register/options',
  );
  const response = await startRegistration({ optionsJSON: options });
  await api.post('/api/passkeys/register/verify', { response, label });
}
